/**
 * Resolve the Pendo identity from the Auth0 access token (spec 094).
 *
 * Kept out of `./pendo.js` so the analytics core stays free of app imports; the
 * only dependency here is `sessionService`, which is itself dependency-free.
 *
 * ── Why not the three sessionService accessors ──────────────────────────────
 * The tracking plan's reference wiring is
 * `Promise.all([getSiteFromToken(), getCBUserId(), isAdmin()])`. Each of those
 * calls `getTokenClaims()`, which fetches the token and re-decodes the JWT from
 * scratch — there is no cached identity object — so that is three decodes for
 * one identity. This resolves the claims ONCE and reads all of them off it.
 *
 * ── Why the `sub` fallback, and why it must match cb-api exactly ────────────
 * `cb2/CLAUDE.md` documents that the Auth0 post-login Action sets `cbuserid` on
 * the ID token only, so cb2's ACCESS token would carry no CB user id. Measured
 * against live access tokens for two vladdemo accounts on 2026-08-24, that is
 * no longer true THERE — `https://centerbase.com/cbuserid` is present and
 * distinct per user. But the documented case is real for a
 * normally-provisioned firm, and it is the same asymmetry that makes
 * TokenLogin fall back to `sub`.
 *
 * So this mirrors `cb-api/src/legacy/client.js:50-51` character for character:
 *
 *     claims['https://centerbase.com/cbuserid']
 *       || (sub.includes('|') ? sub.split('|').pop() : null)
 *
 * That is not defensive padding, it is the join condition. cb-api writes D1
 * `jobs.user_id` from its `auth.userId`, and its server-side Pendo events use
 * `job.user_id` as the visitor id (the tenant prefix was dropped from BOTH
 * sides in spec 097). If the client resolved the id any other way
 * the two sides would disagree on a tenant lacking the claim, and one person
 * would become two Pendo visitors.
 *
 * Deliberately NOT resolved here: admin-ness. The `isadmin` claim is absent
 * from the access token (measured; and cb2/CLAUDE.md says so outright), so
 * `sessionService.isAdmin()` returns false for everyone. See initPendo.
 *
 * ⚠️ Residual risk, unfixable from here: if a tenant's token starts carrying
 * `cbuserid` after a period without it, that firm's history sits under
 * sub-derived ids and its future under GUIDs, and Pendo cannot merge visitors
 * retroactively. Both sides flip together, so they stay consistent with each
 * other — but the series still splits. Recorded in spec 094.
 */

import { getTokenClaims } from '@api/services/sessionService'
import userService from '@api/v1/userService'

const SITE_CLAIM = 'https://centerbase.com/cbsite'
const USER_ID_CLAIM = 'https://centerbase.com/cbuserid'

/**
 * The CB user id, resolved exactly as cb-api resolves it.
 * Exported for the test that pins the two derivations together.
 */
export function resolveCbUserId(claims) {
  const fromClaim = claims?.[USER_ID_CLAIM]
  if (fromClaim) return fromClaim
  const sub = String(claims?.sub ?? '')
  // Mirrors cb-api: a `sub` with no '|' is NOT used. An Auth0 sub is always
  // `provider|id`, so a pipe-less one is not an Auth0 subject and guessing
  // from it would invent an id that matches no CB user.
  return sub.includes('|') ? sub.split('|').pop() : null
}

/**
 * @returns {Promise<{tenant: string, cbUserId: string}|null>} null when the
 * token cannot supply a complete identity — the caller must then NOT
 * initialize, because an anonymous visitor is unattributable noise that still
 * consumes a Pendo seat.
 */
export async function resolvePendoIdentity() {
  const claims = await getTokenClaims()
  // The `cbsite` claim, not `getTenant()`. getTenant() reads the hostname and
  // returns '' on the shared host before a site is chosen, and something
  // meaningless inside the IQ iframe. The claim is also what cb-api uses for
  // `job.tenant`, so this keeps account ids joinable with the server events.
  const tenant = claims?.[SITE_CLAIM] || null
  const cbUserId = resolveCbUserId(claims)
  if (!tenant || !cbUserId) return null
  return { tenant, cbUserId }
}

/**
 * The visitor's `role` — the firm's CB **StaffCategory** ("Partner",
 * "Associate", "Paralegal", …), resolved by cb-api and served on `/users/me`.
 *
 * ── Why this is separate from `resolvePendoIdentity` ────────────────────────
 *
 * `resolvePendoIdentity` reads the token and does NO I/O, which is what lets
 * `App.jsx` call `initPendo` synchronously-ish on boot. This one is a network
 * read, so it is deliberately a different function with a different call site:
 * keeping them separate is what makes "the role can never delay
 * `pendo.initialize`" a property of the code shape rather than of the ordering
 * someone happened to write.
 *
 * ── Why the value is not derived here ───────────────────────────────────────
 *
 * cb-api resolves it from CB (`Get_FieldValue` on `1:1012`) and OMITS the
 * property entirely when the firm has not set one. This function therefore
 * returns `null` for "no role", never `''` — an empty string reads as data in
 * Pendo and would become a segment; an absent property reads as absent.
 *
 * ⚠️ Values are firm-typed free text. Expect "Partner", "Equity Partner" and
 * "PARTNER" to coexist across firms. Nothing normalises them, on purpose — a
 * mapping invented here would be a taxonomy nobody agreed to, and it is a
 * reporting problem to solve in Pendo with the real values in hand.
 *
 * Never throws: every failure — a 500, an offline worker, a shape change — is a
 * `null`, because a missing segmentation property must never cost an analytics
 * session or a page load.
 *
 * @returns {Promise<string|null>}
 */
export async function fetchPendoRole() {
  try {
    const resp = await userService.getMe()
    const role = resp?.data?.role
    if (typeof role !== 'string') return null
    const s = role.trim()
    return s || null
  } catch {
    return null
  }
}
